import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { RelevanceStatusBadge } from "@/relevance-review/RelevanceStatusBadge";
import type { BundleRelevanceStatusesQuery } from "@/relevance-review/useBundleRelevanceStatusesForEncounter";
import { resolveBundleRelevanceStatus } from "@/relevance-review/useBundleRelevanceStatusesForEncounter";
import { BundleAttributesCard } from "./BundleAttributesCard";
import { fileCount, identificationText } from "./bundleText";
import { formatBundleDateTime } from "./formatBundleDateTime";
import type { RawFilesBundleRow } from "./useBundlesForPatient";
import type { SkippedPagesByBundle } from "./useSkippedPagesForEncounter";

interface BundleTabsProps {
  bundles: RawFilesBundleRow[];
  isLoading: boolean;
  error: Error | null;
  selectedBundleId: string | null;
  onSelectBundle: (bundleId: string) => void;
  relevanceStatuses: BundleRelevanceStatusesQuery;
  skippedPages?: SkippedPagesByBundle;
}

function bundleLabel(bundle: RawFilesBundleRow): string {
  return bundle.bundle_type ? `${bundle.source} — ${bundle.bundle_type}` : bundle.source;
}

export function BundleTabs({
  bundles,
  isLoading,
  error,
  selectedBundleId,
  onSelectBundle,
  relevanceStatuses,
  skippedPages,
}: BundleTabsProps) {
  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading bundles…</div>;
  }

  if (error) {
    return <div className="text-destructive">{error.message}</div>;
  }

  if (bundles.length === 0) {
    return <div className="text-sm text-muted-foreground">No raw file bundles for this patient.</div>;
  }

  return (
    <Tabs
      orientation="vertical"
      value={selectedBundleId ?? undefined}
      onValueChange={onSelectBundle}
      className="w-full"
    >
      <TabsList className="flex h-auto w-full flex-col items-stretch justify-start gap-1 bg-transparent p-0">
        {bundles.map((bundle) => {
          const relevanceStatus = resolveBundleRelevanceStatus(bundle.id, relevanceStatuses);

          return (
            <HoverCard key={bundle.id} openDelay={300} closeDelay={250}>
              <HoverCardTrigger asChild>
                <div role="presentation">
                  <TabsTrigger
                    value={bundle.id}
                    className={cn(
                      "h-auto w-full items-start justify-start whitespace-normal rounded-md px-3 py-2 text-left",
                      "data-[state=active]:bg-muted data-[state=active]:shadow-none",
                    )}
                  >
                    <div className="flex w-full flex-col items-start gap-0.5">
                      <div className="flex w-full items-center gap-1.5">
                        <span className="flex-1 text-sm font-medium">{bundleLabel(bundle)}</span>
                        {relevanceStatus && <RelevanceStatusBadge status={relevanceStatus} />}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {bundle.bundle_date ? formatBundleDateTime(bundle.bundle_date) : "—"} ·{" "}
                        {fileCount(bundle.files.length)}
                      </span>
                      {bundle.identification && (
                        <span className="text-xs text-muted-foreground">
                          Identified: {identificationText(bundle.identification)}
                        </span>
                      )}
                    </div>
                  </TabsTrigger>
                </div>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" role="group" aria-label="Bundle details" className="w-96">
                <BundleAttributesCard
                  bundle={bundle}
                  relevanceStatus={relevanceStatus}
                  skippedPagesByFile={skippedPages?.get(bundle.id)}
                />
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
